/**
 * DDNarrativeEditor — review/edit the LLM-drafted DD report narrative (Phase C.2).
 *
 * The narrative is the investor's owned content: this modal lets them edit the
 * draft before saving. It never publishes/shares (sharing is Phase D). Built on
 * the shared AppModal (centered card) per the modal system rules.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { X, Plus } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import type { DDNarrativeDraft, DDNotablePoint } from '@/lib/dd/use-dd-narrative';

const POINT_CYCLE: DDNotablePoint['type'][] = ['strength', 'concern', 'neutral'];
const POINT_LABEL: Record<DDNotablePoint['type'], string> = {
  strength: 'Strength',
  concern: 'Concern',
  neutral: 'Neutral',
};

interface Props {
  visible: boolean;
  onClose: () => void;
  initial: DDNarrativeDraft;
  onSave: (draft: DDNarrativeDraft) => void;
  saving: boolean;
}

export function DDNarrativeEditor({ visible, onClose, initial, onSave, saving }: Props) {
  const colors = useThemeColors();
  const [draft, setDraft] = useState<DDNarrativeDraft>(initial);

  // Reset the editable copy whenever a fresh draft is opened.
  useEffect(() => {
    if (visible) setDraft(initial);
  }, [visible, initial]);

  const pointColor = (type: DDNotablePoint['type']) =>
    type === 'strength' ? colors.success : type === 'concern' ? colors.error : colors.text.secondary;

  const field = (label: string, value: string, onChangeText: (t: string) => void) => (
    <View className="mb-4">
      <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        multiline
        textAlignVertical="top"
        placeholder={`Add ${label.toLowerCase()}…`}
        placeholderTextColor={colors.text.tertiary}
        className="rounded-xl px-3 py-2.5"
        style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, minHeight: 90, fontSize: 14 }}
      />
    </View>
  );

  const footer = (
    <AppModal.Actions>
      <ModalAction label="Discard" variant="neutral" onPress={onClose} disabled={saving} />
      <ModalAction
        label={saving ? 'Saving…' : 'Save'}
        variant="primary"
        disabled={saving}
        onPress={() => onSave(draft)}
      />
    </AppModal.Actions>
  );

  return (
    <AppModal visible={visible} onClose={onClose} title="Review report narrative" footer={footer}>
      <Text style={{ color: colors.text.tertiary }} className="text-xs mb-4">
        AI-drafted from your DD evidence — review and edit before it becomes part of your report. It is
        not shared with the startup.
      </Text>

      {field('Executive Summary', draft.executiveSummary, (t) => setDraft((d) => ({ ...d, executiveSummary: t })))}
      {field('Investment Thesis', draft.investmentThesis, (t) => setDraft((d) => ({ ...d, investmentThesis: t })))}
      {field('Key Risks', draft.keyRisks, (t) => setDraft((d) => ({ ...d, keyRisks: t })))}

      {/* Notable points — typed list editor */}
      <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Notable Points</Text>
      <View className="gap-2 mb-3">
        {draft.notablePoints.map((point, idx) => (
          <View key={`point-${idx}`} className="flex-row items-center gap-2">
            <View style={{ width: 84, borderRadius: 8, overflow: 'hidden', backgroundColor: `${pointColor(point.type)}1A` }}>
              <AnimatedPressable
                onPress={() =>
                  setDraft((d) => {
                    const points = [...d.notablePoints];
                    const next = POINT_CYCLE[(POINT_CYCLE.indexOf(point.type) + 1) % POINT_CYCLE.length];
                    points[idx] = { ...point, type: next };
                    return { ...d, notablePoints: points };
                  })
                }
                style={{ paddingVertical: 8, alignItems: 'center' }}
              >
                <Text style={{ color: pointColor(point.type), fontSize: 11, fontWeight: '600' }} numberOfLines={1}>
                  {POINT_LABEL[point.type]}
                </Text>
              </AnimatedPressable>
            </View>
            <TextInput
              value={point.text}
              onChangeText={(t) =>
                setDraft((d) => {
                  const points = [...d.notablePoints];
                  points[idx] = { ...point, text: t };
                  return { ...d, notablePoints: points };
                })
              }
              placeholder="Point…"
              placeholderTextColor={colors.text.tertiary}
              className="flex-1 rounded-lg px-3"
              style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, height: 40, fontSize: 14 }}
            />
            <View style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', backgroundColor: colors.red.light }}>
              <AnimatedPressable
                onPress={() => setDraft((d) => ({ ...d, notablePoints: d.notablePoints.filter((_, i) => i !== idx) }))}
                style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} color={colors.error} />
              </AnimatedPressable>
            </View>
          </View>
        ))}
      </View>
      <View style={{ alignSelf: 'flex-start', borderRadius: 8, overflow: 'hidden', backgroundColor: colors.surface.secondary }}>
        <AnimatedPressable
          onPress={() => setDraft((d) => ({ ...d, notablePoints: [...d.notablePoints, { type: 'neutral', text: '' }] }))}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8 }}
        >
          <Plus size={14} color={colors.primary[600]} />
          <Text style={{ color: colors.primary[600], fontSize: 13, fontWeight: '600' }}>Add point</Text>
        </AnimatedPressable>
      </View>
    </AppModal>
  );
}
